import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import type { WritingExpressionCategoryId } from '@examflow/exams';
import { BookOpen, GripVertical, Search, X } from 'lucide-react';
import { clampFloatingPanelPosition } from './floatingPanelPosition';
import {
  WRITING_EXPRESSION_CATEGORY_IDS,
  WRITING_EXPRESSION_CATEGORY_LABELS,
  defaultExpressionCategory,
  filterWritingExpressions,
  getWritingExpressionsForLanguage,
  hasWritingExpressionPack,
} from './writingExpressionAidModel';

export type WritingExpressionAidProps = {
  languageId: string | null;
  /** Force-open when parent deep-links from grade weak dimension. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Preferred category when opening (e.g. weak-dimension map). */
  focusCategory?: WritingExpressionCategoryId | null;
  onInsert: (text: string) => void;
  disabled?: boolean;
};

type PanelOffset = { left: number; top: number };

/**
 * Floating「常用表达」scaffold — compact toolbar chip + draggable fixed panel.
 * Does not expand inline (keeps the essay editor in place).
 */
export function WritingExpressionAid({
  languageId,
  open: openControlled,
  onOpenChange,
  focusCategory = null,
  onInsert,
  disabled = false,
}: WritingExpressionAidProps) {
  const baseId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origLeft: number;
    origTop: number;
  } | null>(null);
  const [openUncontrolled, setOpenUncontrolled] = useState(false);
  const [offset, setOffset] = useState<PanelOffset | null>(null);
  const [dragging, setDragging] = useState(false);
  const open = openControlled ?? openUncontrolled;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (openControlled === undefined) setOpenUncontrolled(next);
  };

  const items = useMemo(
    () => getWritingExpressionsForLanguage(languageId),
    [languageId]
  );
  const [category, setCategory] = useState<WritingExpressionCategoryId>(() =>
    defaultExpressionCategory(items, focusCategory)
  );
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setCategory(defaultExpressionCategory(items, focusCategory));
  }, [open, focusCategory, items]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (dragRef.current) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (toggleRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const beginDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const next = offset ?? { left: rect.left, top: rect.top };
    if (!offset) setOffset(next);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origLeft: next.left,
      origTop: next.top,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onDragMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !panel) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const rect = panel.getBoundingClientRect();
    setOffset(
      clampFloatingPanelPosition(
        drag.origLeft + dx,
        drag.origTop + dy,
        rect.width,
        rect.height,
        window.innerWidth,
        window.innerHeight
      )
    );
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
  };

  if (!hasWritingExpressionPack(languageId) || items.length === 0) {
    return null;
  }

  const categoriesWithItems = WRITING_EXPRESSION_CATEGORY_IDS.filter((id) =>
    items.some((item) => item.category === id)
  );
  const visible = filterWritingExpressions(items, {
    category: query.trim() ? null : category,
    query,
  });

  const panelStyle: CSSProperties = offset
    ? { left: offset.left, top: offset.top, right: 'auto', bottom: 'auto' }
    : {};

  const panel =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={`${baseId}-panel`}
            role="dialog"
            aria-modal="false"
            aria-label="常用表达"
            data-testid="writing-expression-aid-panel"
            style={panelStyle}
            className={`fixed z-40 flex w-[min(22rem,calc(100vw-2rem))] max-h-[min(28rem,calc(100vh-5rem))] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-slate-900/5 ${
              offset ? '' : 'bottom-4 right-4 sm:bottom-6 sm:right-6'
            } ${dragging ? 'cursor-grabbing select-none' : ''}`}
          >
            <div
              className={`flex items-center justify-between gap-2 border-b border-slate-100 px-2 py-2 ${
                dragging ? 'cursor-grabbing' : 'cursor-grab'
              }`}
              onPointerDown={beginDrag}
              onPointerMove={onDragMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              data-testid="writing-expression-aid-drag-handle"
              title="拖动移动面板"
            >
              <span className="flex min-w-0 items-center gap-1.5 pl-1 text-sm font-semibold text-slate-900">
                <GripVertical className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                <BookOpen className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
                常用表达
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                onPointerDown={(event) => event.stopPropagation()}
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                aria-label="关闭常用表达"
                data-testid="writing-expression-aid-close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="space-y-2.5 overflow-y-auto px-3 py-3">
              <label className="relative block">
                <span className="sr-only">搜索常用表达</span>
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="搜索表达或中文提示…"
                  className="w-full rounded-md border border-slate-200 bg-slate-50 py-2 pl-8 pr-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-100"
                  data-testid="writing-expression-aid-search"
                />
              </label>

              {!query.trim() ? (
                <div
                  className="flex flex-wrap gap-1.5"
                  role="tablist"
                  aria-label="衔接关系分类"
                >
                  {categoriesWithItems.map((id) => {
                    const selected = id === category;
                    return (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => setCategory(id)}
                        className={`rounded-md px-2 py-1 text-xs font-medium ${
                          selected
                            ? 'bg-primary-600 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {WRITING_EXPRESSION_CATEGORY_LABELS[id]}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <ul className="space-y-1.5" data-testid="writing-expression-list">
                {visible.length === 0 ? (
                  <li className="px-1 py-3 text-sm text-slate-500">没有匹配的表达</li>
                ) : (
                  visible.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onInsert(item.text)}
                        className="flex w-full flex-col items-start gap-0.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:border-primary-300 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="text-sm font-medium text-slate-900">{item.text}</span>
                        {item.gloss ? (
                          <span className="text-xs text-slate-500">{item.gloss}</span>
                        ) : null}
                        {query.trim() ? (
                          <span className="text-[11px] text-slate-400">
                            {WRITING_EXPRESSION_CATEGORY_LABELS[item.category]}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="inline-flex" data-testid="writing-expression-aid">
      <button
        ref={toggleRef}
        type="button"
        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
          open
            ? 'bg-primary-600 text-white'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
        }`}
        aria-expanded={open}
        aria-controls={`${baseId}-panel`}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        data-testid="writing-expression-aid-toggle"
      >
        <BookOpen className="h-3.5 w-3.5" aria-hidden />
        常用表达
      </button>
      {panel}
    </div>
  );
}
